import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Save,
  PlayCircle,
  Filter,
  Search,
  BarChart3,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { certificationApiService } from '@/services/api/certification-api.service';
import { QuestionCard } from '@/components/certifications/QuestionCard';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { QuestionEditorModal } from '@/components/certifications/QuestionEditorModal';
import { DifficultyBadge } from '@/components/certifications/DifficultyBadge';

type QuestionFilterType = 'ALL' | 'EASY' | 'MEDIUM' | 'HARD' | 'AI_GENERATED' | 'MANUAL';

export default function ExamEditor() {
  const { id, examId } = useParams<{ id: string; examId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [filter, setFilter] = useState<QuestionFilterType>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  // Fetch exam data
  const { data: exam, isLoading } = useQuery({
    queryKey: ['exam', examId || id],
    queryFn: async () => {
      if (examId) {
        return certificationApiService.getExamById(examId);
      } else if (id) {
        const cert = await certificationApiService.getCertificationById(id);
        if (cert.certification_exam_id) {
          return certificationApiService.getExamById(cert.certification_exam_id);
        }
      }
      throw new Error('No exam found');
    },
    enabled: !!(examId || id),
  });

  // Update questions mutation
  const updateMutation = useMutation({
    mutationFn: (questions: any[]) =>
      certificationApiService.updateExamQuestions(exam!._id, { questions }),
    onSuccess: () => {
      toast.success('Exam updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['exam'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update exam');
    },
  });

  // Publish exam mutation
  const publishMutation = useMutation({
    mutationFn: () => certificationApiService.publishExam(exam!._id),
    onSuccess: () => {
      toast.success('Exam published successfully!');
      queryClient.invalidateQueries({ queryKey: ['exam'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to publish exam');
    },
  });

  const questions = exam?.questions || [];

  // Filter questions
  const filteredQuestions = questions.filter((q: any) => {
    // Apply difficulty/source filter
    if (filter !== 'ALL') {
      if (['EASY', 'MEDIUM', 'HARD'].includes(filter)) {
        if (q.difficulty_level !== filter) return false;
      } else {
        if (q.source !== filter) return false;
      }
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        q.question_text.toLowerCase().includes(query) ||
        q.topic?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  const handleAddQuestion = () => {
    setSelectedQuestion(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditQuestion = (question: any) => {
    setSelectedQuestion(question);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDeleteQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter((q: any) => q.id !== questionId);
    updateMutation.mutate(updatedQuestions);
  };

  const handleSaveQuestion = (questionData: any) => {
    let updatedQuestions;

    if (modalMode === 'create') {
      // Add new question
      updatedQuestions = [
        ...questions,
        {
          ...questionData,
          id: Date.now().toString(),
          order: questions.length + 1,
          created_at: new Date(),
          updated_at: new Date(),
        },
      ];
    } else {
      // Update existing question
      updatedQuestions = questions.map((q: any) =>
        q.id === questionData.id
          ? { ...q, ...questionData, updated_at: new Date() }
          : q
      );
    }

    updateMutation.mutate(updatedQuestions);
  };

  const handlePublish = () => {
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = () => {
    publishMutation.mutate();
    setIsPublishModalOpen(false);
  };

  // Calculate statistics
  const stats = {
    total: questions.length,
    easy: questions.filter((q: any) => q.difficulty_level === 'EASY').length,
    medium: questions.filter((q: any) => q.difficulty_level === 'MEDIUM').length,
    hard: questions.filter((q: any) => q.difficulty_level === 'HARD').length,
    ai_generated: questions.filter((q: any) => q.source === 'AI_GENERATED').length,
    manual: questions.filter((q: any) => q.source === 'MANUAL').length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Exam not found</p>
          <button
            onClick={() => navigate('/certifications')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Certifications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/certifications/${id || exam.certification_id}`)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Certification
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{exam.title}</h1>
            <p className="text-gray-600">{exam.description}</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleAddQuestion}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Question</span>
            </button>

            {!exam.is_published && (
              <button
                onClick={handlePublish}
                disabled={publishMutation.isLoading || questions.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <PlayCircle className="w-4 h-4" />
                <span>Publish Exam</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Exam Statistics</h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
            <p className="text-sm text-gray-600">Total Questions</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600">{stats.easy}</p>
            <p className="text-sm text-gray-600">Easy</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-600">{stats.medium}</p>
            <p className="text-sm text-gray-600">Medium</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">{stats.hard}</p>
            <p className="text-sm text-gray-600">Hard</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.ai_generated}</p>
            <p className="text-sm text-gray-600">AI Generated</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-600">{stats.manual}</p>
            <p className="text-sm text-gray-600">Manual</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions by text or topic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-500" />
            <button
              onClick={() => setFilter('ALL')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('EASY')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'EASY' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Easy
            </button>
            <button
              onClick={() => setFilter('MEDIUM')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'MEDIUM' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Medium
            </button>
            <button
              onClick={() => setFilter('HARD')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'HARD' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Hard
            </button>
            <button
              onClick={() => setFilter('AI_GENERATED')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'AI_GENERATED' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              AI Generated
            </button>
            <button
              onClick={() => setFilter('MANUAL')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${filter === 'MANUAL' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Manual
            </button>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Plus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || filter !== 'ALL' ? 'No questions match your filters' : 'No questions yet'}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || filter !== 'ALL'
                ? 'Try adjusting your search or filter criteria'
                : 'Start by adding your first question'}
            </p>
            {!searchQuery && filter === 'ALL' && (
              <button
                onClick={handleAddQuestion}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Question
              </button>
            )}
          </div>
        ) : (
          filteredQuestions.map((question: any, index: number) => (
            <QuestionCard
              key={question.id}
              question={question}
              index={index}
              onEdit={handleEditQuestion}
              onDelete={handleDeleteQuestion}
            />
          ))
        )}
      </div>

      {/* Question Editor Modal */}
      <QuestionEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveQuestion}
        question={selectedQuestion}
        mode={modalMode}
      />

      {/* Publish Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={handleConfirmPublish}
        title="Publish Exam"
        message="Are you sure you want to publish this exam? It will become available to users."
        confirmButtonText="Publish"
        isLoading={publishMutation.isPending}
      />
    </div>
  );
}
